import React from 'react';
import { CalendarClock, AlertTriangle, Clock, CheckCircle2, Eye, ShieldCheck, Info } from 'lucide-react';
import { formatSnapshotDate } from '../utils/dateUtils';

function toneClasses(tone) {
  const map = {
    green: 'border-signal-green/30 bg-signal-green/5 text-signal-green',
    yellow: 'border-signal-yellow/35 bg-signal-yellow/5 text-signal-yellow',
    red: 'border-signal-red/35 bg-signal-red/5 text-signal-red',
    blue: 'border-mentor-accent/35 bg-mentor-accent/5 text-mentor-accent',
  };
  return map[tone] || map.blue;
}

function eventIcon(status) {
  if (status === 'today') return AlertTriangle;
  if (status === 'recent') return CheckCircle2;
  return CalendarClock;
}

function PlainEnglishBlock({ icon: Icon, title, children }) {
  return (
    <div className="rounded-xl border border-mentor-border/35 bg-mentor-bg/35 p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} className="text-mentor-accent" />
        <p className="text-[10px] uppercase tracking-widest font-display font-bold text-mentor-muted">{title}</p>
      </div>
      <p className="text-xs text-mentor-text/90 font-body leading-relaxed">{children}</p>
    </div>
  );
}

export default function MacroEventAwareness({ macro }) {
  if (!macro?.enabled) return null;

  const tone = macro.tone || 'blue';
  const events = Array.isArray(macro.events) ? macro.events.slice(0, 4) : [];
  const noEventMessage = macro.noEventMessage || 'No major macro event pressure detected today.';
  const whyItMatters = macro.whyItMatters || 'Major calendar events can briefly shake yields, inflation expectations, credit spreads, or volatility.';
  const bondMentorResponse = macro.bondMentorResponse || macro.confirmationNote || 'Bond Mentor keeps the main signal tied to confirmed market data and avoids reacting to one noisy headline.';
  const userWatch = macro.userWatch || 'Watch whether the move is still visible after the event window passes.';

  return (
    <div className="glass-card p-5 border border-mentor-border/40">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${toneClasses(tone)}`}>
            <CalendarClock size={20} />
          </div>
          <div>
            <p className="text-[10px] font-display font-bold uppercase tracking-widest text-mentor-muted mb-1">
              Macro Event Context
            </p>
            <h3 className="font-display font-bold text-lg text-mentor-text">
              {events.length ? (macro.label || 'Macro Calendar Context') : noEventMessage}
            </h3>
            <p className="text-sm text-mentor-muted font-body leading-relaxed max-w-3xl mt-1">
              {macro.summary || 'Bond Mentor checks whether today’s market move is happening near a major macro event.'}
            </p>
          </div>
        </div>

        <div className={`px-3 py-2 rounded-xl border text-right min-w-[190px] ${toneClasses(tone)}`}>
          <p className="text-[10px] uppercase tracking-widest font-display font-bold opacity-80">Event Risk</p>
          <p className="text-sm font-display font-bold">{macro.riskLabel || 'Quiet Calendar'}</p>
          <p className="text-[10px] font-mono mt-1 text-mentor-muted">Window: {macro.windowLabel || 'Normal'}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <PlainEnglishBlock icon={Info} title="Why it matters">
          {whyItMatters}
        </PlainEnglishBlock>
        <PlainEnglishBlock icon={ShieldCheck} title="What Bond Mentor does">
          {bondMentorResponse}
        </PlainEnglishBlock>
        <PlainEnglishBlock icon={Eye} title="What to watch">
          {userWatch}
        </PlainEnglishBlock>
      </div>

      {macro.confirmationNote && events.length > 0 && (
        <div className={`rounded-xl border p-3 mb-4 ${toneClasses(tone)}`}>
          <p className="text-xs font-body leading-relaxed text-mentor-text/90">
            <strong className="font-display">Signal handling:</strong> {macro.confirmationNote}
          </p>
        </div>
      )}

      {events.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-3">
          {events.map((event, index) => {
            const Icon = eventIcon(event.status);
            return (
              <div key={`${event.type}-${event.date}-${index}`} className="rounded-xl border border-mentor-border/35 bg-mentor-bg/35 p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <Icon size={15} className={event.impact === 'high' ? 'text-signal-yellow' : 'text-mentor-accent'} />
                    <h4 className="font-display font-bold text-sm text-mentor-text">{event.name}</h4>
                  </div>
                  <span className="text-[10px] font-mono text-mentor-muted whitespace-nowrap">
                    {formatSnapshotDate(event.date)}
                  </span>
                </div>
                <p className="text-xs text-mentor-muted font-body leading-relaxed mb-2">{event.description}</p>
                {event.watchNote && (
                  <p className="text-[11px] text-mentor-text/85 font-body leading-relaxed mb-2">
                    <strong>Watch:</strong> {event.watchNote}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-display font-semibold border border-mentor-border/30 text-mentor-muted uppercase">
                    {event.statusLabel}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-display font-semibold border border-mentor-border/30 text-mentor-muted uppercase">
                    {event.impactLabel}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl border border-signal-green/20 bg-signal-green/5 p-4 flex items-start gap-3">
          <CheckCircle2 size={18} className="text-signal-green mt-0.5" />
          <div>
            <p className="text-sm text-mentor-text/90 font-display font-semibold">{noEventMessage}</p>
            <p className="text-xs text-mentor-muted font-body leading-relaxed mt-1">
              Bond Mentor is reading the market mostly from the yield curve, credit spreads, volatility, liquidity, Fed/bond agreement, and global bond signals.
            </p>
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-mentor-border/25 flex items-start gap-2">
        <Clock size={14} className="text-mentor-muted mt-0.5" />
        <p className="text-[11px] text-mentor-muted font-body leading-relaxed">
          Macro events are context only. They help explain why yields, credit, volatility, or liquidity may move around important calendar windows. They do not override the final signal by themselves; the main advice changes only when confirmed market signals support it.
        </p>
      </div>
    </div>
  );
}
